import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { VideoCamera } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useIncomingCalls, type IncomingCall } from '../hooks/useIncomingCalls';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { firstNameOf } from '../utils/liveLessonText';
import { bannerJoin, bannerLine } from '../utils/incomingCall';
import { stopRinging } from '../services/liveLessons';
import { VideoCallModal } from './VideoCallModal';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM } from './buttonStyles';

export function IncomingCallBanner() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const calls = useIncomingCalls();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [joining, setJoining] = useState<IncomingCall | null>(null);

  // Not in the lesson's own room: its left screen already has Rejoin. Not on its lesson page either:
  // that page's Join lights and its status line says who is on the call, and the pill would sit
  // over its Back (decision 72).
  const visible = calls.filter((c) => !dismissed.has(c.roomId)
    && !(c.bookingId && (pathname === `/call/${c.bookingId}` || pathname === `/live/${c.bookingId}`)));
  const call = visible[0];
  // Whoever is in the call, named, whichever side started the room (decision 72).
  const inCall = useUserIdentity(call?.presentUid);
  if (!currentUser) return null;
  if (visible.length === 0 && !joining) return null;
  const name = inCall?.name ?? (call?.presentUid === call?.initiatorUid ? call?.initiatorName : '') ?? '';

  const dismiss = (roomId: string) =>
    setDismissed((prev) => new Set([...prev, roomId]));
  const join = call ? bannerJoin(call) : null;

  return (
    <>
      {call && !joining && (
        <div
          // Below the safe area on a phone; on a larger screen 84, clear of
          // the app's 72-high sticky top bar (as BackButton's floating one).
          className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4 top-[max(calc(env(safe-area-inset-top,0px)+12px),12px)] md:top-[84px]"
        >
          {/* MT-1's light material: it floats over the app's content. */}
          <div
            className="pointer-events-auto flex h-14 w-full max-w-[480px] items-center gap-2 rounded-full border border-[rgba(0,0,0,0.06)] bg-[rgba(255,255,255,0.72)] pl-3.5 pr-2 shadow-[0_8px_24px_rgba(0,0,0,0.12)] backdrop-blur-[20px]"
            style={{ WebkitBackdropFilter: 'blur(20px)' }}
          >
            {/* Pulsing green dot: the call is live. Still under reduced motion. */}
            <span className="relative flex-shrink-0 w-3 h-3">
              <span
                className="absolute inset-0 rounded-full motion-safe:animate-ping opacity-75"
                style={{ backgroundColor: '#34C759' }}
              />
              <span
                className="relative block w-3 h-3 rounded-full"
                style={{ backgroundColor: '#34C759' }}
              />
            </span>

            <VideoCamera size={16} weight="fill" className="flex-shrink-0 text-db-text-2" />

            {/* A lesson: "Ana is on the call", the Action card's words (165). A call outside a lesson: the
                name, then what they are doing, in the secondary label. */}
            {call.bookingId ? (
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere] text-[15px] leading-[20px] font-semibold text-db-text-1" data-banner-line>
                {bannerLine(firstNameOf(name, ''))}
              </span>
            ) : (
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="[overflow-wrap:anywhere] text-[15px] leading-[20px] font-semibold text-db-text-1">
                  {name || call.initiatorName}
                </span>
                <span className="text-[13px] leading-[18px] text-db-text-2">Calling you</span>
              </span>
            )}

            {/* Dismiss: the shared 36 secondary, its target grown to 44 */}
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(call.roomId)}
              className={`${BTN_SECONDARY_SM} relative flex-shrink-0 px-2.5 after:absolute after:-inset-1 after:content-['']`}
            >
              Dismiss
            </button>

            {/* Join, the primary: the shared 36 primary, its target grown to 44. A lesson's stops the ring on this
                person's phone first (142, 150; booking-contracts.md 12). */}
            <button
              type="button"
              onClick={() => {
                dismiss(call.roomId);
                if (join?.stopRingFor) stopRinging({ bookingId: join.stopRingFor }).catch(() => { /* nothing ringing, or not this person's */ });
                // A lesson opens in its room page, with its states around the
                // call; a pair's call outside any lesson opens the pair's room
                // (rule 9); a conversation's call opens over this screen.
                if (join?.to) navigate(join.to);
                else setJoining(call);
              }}
              className={`${BTN_PRIMARY_SM} relative flex-shrink-0 px-2.5 after:absolute after:-inset-1 after:content-['']`}
              data-banner-join
            >
              {join?.label ?? 'Join'}
            </button>
          </div>
        </div>
      )}

      {joining && (
        <VideoCallModal
          bookingId={joining.bookingId}
          conversationId={joining.bookingId ? undefined : joining.conversationId}
          participantId={currentUser.uid}
          displayName={currentUser.displayName ?? currentUser.email ?? 'User'}
          otherParticipantName={joining.initiatorName}
          otherParticipantId={joining.initiatorUid}
          onClose={() => setJoining(null)}
        />
      )}
    </>
  );
}
